import { AuxButton } from "@blk/aladdin-react-components-es";
import { AuxButtonSizeEnum, AuxButtonTypeEnum } from "@blk/aladdin-web-components";
import { saveAs } from "file-saver";
import { isEqual } from "lodash";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { requestAsync } from "redux-query";
import { createSelector } from "reselect";
import { PREQIN_BENCHMARK_SOURCE, PREQIN_INDEX_SOURCE } from "./insPrivateEquityStandardContent";
import { exportPrivateBenchmarks } from "api/admin/src";
import { getCurrentLocalTime } from "common/date-utils";
import { InsInlineMenu } from "components/custom/insInlineMenu";
import { isAccessPreqinProEnabled, isPermissionsEnabled, isPreqinClientEnabled } from "hooks/use-tenant-settings";
import { language } from "language/default";
import { appInsightsTrackEvent } from "plugins/app-insight-init";
const PREQIN_SOURCES = [PREQIN_BENCHMARK_SOURCE.toLowerCase(), PREQIN_INDEX_SOURCE.toLowerCase(), "preqin"];
const makeSelectHasPreqinBenchmarkSelected = (stateId) => createSelector((state) => state.grids.state[stateId]?.selectedRows, (state) => state.entities.PrivateBenchmarks, (selectedRows, benchmarks) => {
    if (!selectedRows?.length || !benchmarks) {
        return false;
    }
    return selectedRows.some((id) => {
        const source = benchmarks[id]?.summary?.source?.toLowerCase().trim();
        return source ? PREQIN_SOURCES.includes(source) : false;
    });
});
const withOptions = (exportExcelQueryConfig) => {
    exportExcelQueryConfig.force = true;
    exportExcelQueryConfig.options = exportExcelQueryConfig.options || {};
    exportExcelQueryConfig.options.headers = exportExcelQueryConfig.options.headers || {};
    exportExcelQueryConfig.options.headers.requestBlob = "true";
    exportExcelQueryConfig.meta = {
        ...{
            errorMessage: language.en.grid.excelExport.downloadExcelFailed,
            pendingMessage: language.en.grid.excelExport.downloadExcelPending,
            successMessage: language.en.grid.excelExport.downloadExcelSucceeded
        }
    };
    return exportExcelQueryConfig;
};
const getExportQueryConfig = (isCustom, selectedBenchmarks, fileName, lastQuarterOnly) => {
    const exportConfig = exportPrivateBenchmarks({ privateBenchmarkIdentifier: selectedBenchmarks.map((uid) => ({ isCustom, uid })) }, {
        transform: (response) => {
            if (response?.size > 0) {
                saveAs(response, fileName);
            }
            return {};
        }
    });
    const configuredExport = withOptions(exportConfig);
    const separator = configuredExport.url.includes('?') ? '&' : '?';
    configuredExport.url = `${configuredExport.url}${separator}lastQuarterOnly=${lastQuarterOnly}`;
    return configuredExport;
};
const createExportHandler = (dispatch, isCustom, selectedBenchmarks, fileName, appInsightsOperationName, lastQuarterOnly) => () => {
    appInsightsTrackEvent({
        name: appInsightsOperationName,
        properties: {
            BenchmarksCount: selectedBenchmarks.length,
            LastQuarterOnly: lastQuarterOnly,
            LocalTimestamp: getCurrentLocalTime()
        }
    });
    dispatch(requestAsync(getExportQueryConfig(isCustom, selectedBenchmarks, fileName, lastQuarterOnly)));
};
const createMainButton = (label, isDisabled, tooltip, onClick) => React.createElement("span", { title: tooltip },
    React.createElement(AuxButton, { icon: "download", size: AuxButtonSizeEnum.SMALL, label: label, type: AuxButtonTypeEnum.SECONDARY, isDisabled: isDisabled, onClick: onClick, style: isDisabled ? { pointerEvents: "none" } : undefined }));
export const getExportButton = (params) => {
    const clickHandler = createExportHandler(params.dispatch, params.isCustom, params.selectedBenchmarks, params.fileName, params.appInsightsOperationName, false);
    return createMainButton(params.label, params.isDisabled, params.tooltip ?? "", clickHandler);
};
const getSplitExportButton = (params) => {
    const fullExportHandler = createExportHandler(params.dispatch, params.isCustom, params.selectedBenchmarks, params.fileName, params.appInsightsOperationName, false);
    const lastQuarterHandler = createExportHandler(params.dispatch, params.isCustom, params.selectedBenchmarks, params.fileName, params.appInsightsOperationName, true);
    const indicesLang = language.en.administration.marketDataAdministration.indices;
    const menuItems = [
        {
            action: lastQuarterHandler,
            label: indicesLang.privateEquityBenchmarks.exportLastQuarter
        }
    ];
    return (React.createElement("div", { className: "privateEquityBenchmarksExportSplitButton" },
        createMainButton(params.label, params.isDisabled, params.tooltip ?? "", fullExportHandler),
        React.createElement(InsInlineMenu, { icon: "arrow-dropdown", isDisabled: params.isDisabled, items: menuItems, label: "" })));
};
export const InsDownloadPrivateEquityBenchmarks = (props) => {
    const dispatch = useDispatch();
    const preqinPro = isAccessPreqinProEnabled() && isPreqinClientEnabled() && isPermissionsEnabled();
    const indicesLang = language.en.administration.marketDataAdministration.indices;
    const selectedBenchmarks = useSelector((state) => state.grids.state[props.stateId]?.selectedRows?.filter(Boolean) || [], isEqual);
    const hasPreqinSelected = useSelector(makeSelectHasPreqinBenchmarkSelected(props.stateId));
    const preqinBlocked = hasPreqinSelected && !preqinPro;
    const label = props.templateOnly
        ? indicesLang.privateEquityBenchmarks.downloadTemplate
        : indicesLang.privateEquityBenchmarks.exportBenchmarks;
    const isDisabled = preqinBlocked || (!props.templateOnly && selectedBenchmarks.length === 0);
    const benchmarks = props.templateOnly ? [] : selectedBenchmarks;
    const fileName = `${props.templateOnly ? "BenchmarksTemplate" : "BenchmarksExport"}.xlsx`;
    const commonParams = {
        appInsightsOperationName: props.appInsightsOperationName,
        dispatch,
        fileName,
        isCustom: props.isCustom,
        isDisabled,
        label,
        selectedBenchmarks: benchmarks,
        tooltip: preqinBlocked ? indicesLang.privateEquityBenchmarks.preqinExportBlockedTooltip : ""
    };
    const shouldShowSplitButton = !props.templateOnly && !props.isCustom;
    return props.templateOnly || !shouldShowSplitButton
        ? getExportButton(commonParams)
        : getSplitExportButton(commonParams);
};
